/**
 * MayaCuratingIndicator — iter281 (Feb 14, 2026)
 *
 * Replaces the generic "Maya is thinking…" loading line with a
 * boutique-flavoured curating moment. While Maya's backend reply is
 * in flight (typically 8–25 seconds for the LLM round-trip + the
 * fabric/option look-up), the customer sees:
 *
 *   1. Maya's portrait — gently bobbing (like she's leaning in to think)
 *   2. A "running-stitch" needle animation — a tiny needle dot moves
 *      along a curved path, leaving a trail of dashes (stitches)
 *      behind it. Loops every ~2.4 seconds.
 *   3. Rotating narrator copy — every 1.8s the line changes to feel
 *      like Maya is narrating her actual process:
 *         "Browsing our fabric shelves…"
 *         "Pairing colours that flatter you…"
 *         "Adding her boutique touch…"
 *         "Almost ready with your picks…"
 *
 * Why this works:
 *   • Customers stop seeing it as "AI loading" and start seeing it
 *     as "a real designer at work" — exactly the brand voice.
 *   • The rotating copy creates anticipation and reduces perceived
 *     wait time (a classic UX trick: narrating progress > silence).
 *   • The stitching motif ties the loading state to the actual
 *     craft — JnS is a tailoring boutique, not a chatbot startup.
 *
 * Props:
 *   • phrases   — optional array of phrases to rotate through. Pass
 *                 step-specific ones (e.g. Step 4 might say "Maya's
 *                 tape measure is unrolling…"). Defaults to a Step-2-
 *                 friendly fabric/style set.
 *   • intervalMs - milliseconds between phrase rotations. Default 1800.
 *
 * Reduced motion: respects `prefers-reduced-motion` — falls back to
 * a single static "Maya is curating your picks" line with no needle
 * animation.
 */
import { useEffect, useState } from "react";
import { MAYA_PORTRAIT_URL } from "../../../../../lib/mayaPortrait";

const DEFAULT_PHRASES = [
    "Browsing our fabric shelves…",
    "Pairing colours that flatter you…",
    "Cross-checking with your budget…",
    "Adding her boutique touch…",
    "Almost ready with your picks…",
];

export default function MayaCuratingIndicator({
    phrases = DEFAULT_PHRASES,
    intervalMs = 1800,
}) {
    const [idx, setIdx] = useState(0);

    useEffect(() => {
        if (!phrases.length) return;
        const t = setInterval(() => {
            setIdx((i) => (i + 1) % phrases.length);
        }, intervalMs);
        return () => clearInterval(t);
    }, [phrases.length, intervalMs]);

    const current = phrases[idx] || phrases[0] || "Maya is curating your picks…";

    return (
        <div
            className="flex items-start gap-2.5 py-1.5"
            data-testid="maya-curating-indicator"
            role="status"
            aria-live="polite"
            aria-label={`Maya is working — ${current}`}
        >
            {/* Maya's portrait — subtle bob */}
            <div className="relative shrink-0">
                <span
                    className="block w-9 h-9 rounded-full overflow-hidden border-2 border-white shadow-sm"
                    style={{ animation: "maya-curate-bob 2.2s ease-in-out infinite" }}
                >
                    <img
                        src={MAYA_PORTRAIT_URL}
                        alt=""
                        aria-hidden
                        className="w-full h-full object-cover"
                    />
                </span>
                {/* Soft rosegold halo pulsing behind the portrait */}
                <span
                    className="absolute inset-0 rounded-full bg-rosegold/35 pointer-events-none"
                    style={{ animation: "maya-curate-halo 2.2s ease-in-out infinite" }}
                    aria-hidden
                />
            </div>

            {/* Right column — stitch SVG + rotating phrase */}
            <div className="flex flex-col gap-1 min-w-0">
                <StitchAnimation />
                <p
                    key={idx}
                    className="text-[12px] sm:text-[12.5px] text-ink-soft italic leading-relaxed"
                    style={{ animation: "maya-curate-phrase 480ms cubic-bezier(0.22, 1, 0.36, 1) both" }}
                >
                    {current}
                </p>
            </div>

            <style>{`
                /* Portrait gently leans in — like she's looking at her shelf */
                @keyframes maya-curate-bob {
                    0%, 100% { transform: translateY(0) rotate(0); }
                    50%      { transform: translateY(-1.5px) rotate(-2deg); }
                }
                /* Rosegold halo pulses with a soft breath */
                @keyframes maya-curate-halo {
                    0%, 100% { opacity: 0; transform: scale(1); }
                    50%      { opacity: 1; transform: scale(1.35); }
                }
                /* Rotating phrase eases in from below */
                @keyframes maya-curate-phrase {
                    from { opacity: 0; transform: translateY(4px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                /* Stitch dashes flowing along the curve */
                @keyframes maya-curate-stitch-dash {
                    from { stroke-dashoffset: 0; }
                    to   { stroke-dashoffset: -24; }
                }
                /* Needle dot orbits along the path */
                @keyframes maya-curate-needle {
                    0%   { offset-distance: 0%;   opacity: 0; }
                    10%  { opacity: 1; }
                    90%  { opacity: 1; }
                    100% { offset-distance: 100%; opacity: 0; }
                }
                @media (prefers-reduced-motion: reduce) {
                    [data-testid="maya-curating-indicator"] * {
                        animation: none !important;
                    }
                }
            `}</style>
        </div>
    );
}


/**
 * Compact SVG that draws a running-stitch animation:
 *   • A wavy curve = the fabric edge being stitched
 *   • Dashed stroke that slowly slides leftward = the line of stitches
 *   • A small filled circle that moves along the curve = the needle
 *
 * The whole strip is 120×16 — fits snugly above the rotating phrase
 * without dominating the chat.
 */
function StitchAnimation() {
    // Curve goes left→right with a gentle dip and rise — like a hem.
    const PATH = "M 2 8 Q 30 0, 60 8 T 118 8";
    return (
        <svg
            viewBox="0 0 120 16"
            width="120"
            height="16"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            style={{ display: "block" }}
        >
            {/* Faint cream guide so the stitch line reads on cream bg */}
            <path
                d={PATH}
                fill="none"
                stroke="rgba(140,80,40,0.12)"
                strokeWidth="1.5"
                strokeLinecap="round"
            />
            {/* The dashed "stitch" line that animates leftward */}
            <path
                d={PATH}
                fill="none"
                stroke="#8C4A2C"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeDasharray="3 3"
                style={{ animation: "maya-curate-stitch-dash 1.6s linear infinite" }}
            />
            {/* The "needle" — a small bright dot that orbits the path */}
            <circle
                r="2.2"
                fill="#E8A063"
                style={{
                    offsetPath: `path("${PATH}")`,
                    animation: "maya-curate-needle 2.4s ease-in-out infinite",
                }}
            />
            {/* A tiny pin head — sits above the needle for extra craft feel */}
            <circle
                r="1.2"
                fill="#FFFCF7"
                stroke="#B9663E"
                strokeWidth="0.5"
                style={{
                    offsetPath: `path("${PATH}")`,
                    animation: "maya-curate-needle 2.4s ease-in-out infinite",
                }}
            />
        </svg>
    );
}
